/**
 * ChatToastCoordinator - Singleton service for managing chat message toasts
 * 
 * Purpose: Prevents toast spam by debouncing notifications and tracking window visibility
 * Industry Best Practices: Based on Slack, Discord, and Teams notification patterns
 * 
 * Features:
 * - Debouncing: Max 1 toast per conversation per 5 seconds
 * - Window awareness: Only shows toasts for closed/minimized windows
 * - Automatic cleanup on window open
 */

interface ToastState {
  lastShownAt: number;
  timeoutId?: NodeJS.Timeout;
}

class ChatToastCoordinator {
  private static instance: ChatToastCoordinator;
  private toastStates: Map<string, ToastState> = new Map();
  private readonly DEBOUNCE_MS = 5000; // 5 seconds between toasts per conversation

  private constructor() {}

  static getInstance(): ChatToastCoordinator {
    if (!ChatToastCoordinator.instance) {
      ChatToastCoordinator.instance = new ChatToastCoordinator();
    }
    return ChatToastCoordinator.instance;
  }

  /**
   * Check if a toast can be shown for a conversation
   * Implements rate limiting to prevent spam
   */
  canShowToast(conversationId: string): boolean {
    const state = this.toastStates.get(conversationId);
    
    if (!state) {
      return true; // First toast for this conversation
    }

    const timeSinceLastToast = Date.now() - state.lastShownAt;
    return timeSinceLastToast >= this.DEBOUNCE_MS;
  }

  /**
   * Mark a toast as shown for a conversation
   * Starts the debounce timer
   */
  markToastShown(conversationId: string): void {
    // Clear existing timeout if any
    const existingState = this.toastStates.get(conversationId);
    if (existingState?.timeoutId) {
      clearTimeout(existingState.timeoutId);
    }

    // Set new state
    this.toastStates.set(conversationId, {
      lastShownAt: Date.now(),
    });
  }

  /**
   * Clear toast state when a chat window is opened
   * Allows immediate toasts once window is closed again
   */
  clearToast(conversationId: string): void {
    const state = this.toastStates.get(conversationId);
    if (state?.timeoutId) {
      clearTimeout(state.timeoutId);
    }
    this.toastStates.delete(conversationId);
  }

  /**
   * Clear all toast states (useful for cleanup/logout)
   */
  clearAll(): void {
    this.toastStates.forEach(state => {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
    });
    this.toastStates.clear();
  }
}

// Export singleton instance
export const chatToastCoordinator = ChatToastCoordinator.getInstance();
